<template>
  <div class="bg-white shadow-xl rounded-lg p-8 max-w-md w-full">
    <h2 class="text-2xl font-bold text-foreground mb-6 text-center">Login</h2>
    
    <form @submit.prevent="handleLogin">
      <div class="mb-4">
        <label for="email" class="block text-foreground text-sm font-medium mb-2">Email</label>
        <input
          type="email"
          id="email"
          v-model="email"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Enter your email"
          required
        >
      </div>
      
      <div class="mb-6">
        <label for="password" class="block text-foreground text-sm font-medium mb-2">Password</label>
        <input
          type="password"
          id="password"
          v-model="password"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Enter your password"
          required
        >
      </div>
      
      <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
        {{ error.message }}
      </div>
      
      <button
        type="submit"
        class="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out mb-4"
        :disabled="loading"
      >
        <span v-if="loading">Logging in...</span>
        <span v-else>Login</span>
      </button>
      
      <div class="text-center">
        <button
          type="button"
          @click="switchToRegister"
          class="text-primary hover:text-primary/80 text-sm font-medium"
        >
          Don't have an account? Register
        </button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useAuth } from '@/composables/useAuth';
import type { LoginCredentials } from '@/types/user';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'LoginForm',
  emits: ['login-success', 'switch-to-register'],
  setup(props, { emit }) {
    const { login, loading, error } = useAuth();
    
    const email = ref('');
    const password = ref('');
    
    async function handleLogin() {
      if (loading.value) return;
      
      try {
        const credentials: LoginCredentials = {
          email: email.value,
          password: password.value
        };
        
        await login(credentials);
        
        emit('login-success', email.value);
        
        // Reset form
        email.value = '';
        password.value = '';
      } catch (err) {
        // Error is already handled in the useAuth composable
        logger.error('Login failed', LogCategory.AUTH, { error: err });
      }
    }
    
    function switchToRegister() {
      emit('switch-to-register');
    }
    
    return {
      email,
      password,
      loading,
      error,
      handleLogin,
      switchToRegister
    };
  }
});
</script>